/**
 * Carries the File[] selected on the home / new-project prompt panel
 * across the client-side router.push() into /project/[id], where the
 * upload to /api/projects/[id]/attachments actually happens (the
 * upload endpoint needs a project row to exist, which doesn't until
 * the project page boots).
 *
 * Module-level — survives a soft nav because the JS heap persists.
 * Falls back to "no attachments" on a hard reload, which is acceptable:
 * the user can re-pick on the chat panel.
 */

let pending: File[] | null = null;

export function setPendingAttachments(files: File[]): void {
  pending = files.length > 0 ? files : null;
}

export function takePendingAttachments(): File[] {
  const out = pending ?? [];
  pending = null;
  return out;
}

export function hasPendingAttachments(): boolean {
  return (pending?.length ?? 0) > 0;
}

/**
 * How many pending files the BUILD MODEL will actually be able to SEE, WITHOUT
 * draining the queue.
 *
 * The intent gate runs before the upload does, and it has to know whether this
 * first message carries a design reference — a picture answers "what's the
 * website for?" and a gate that cannot see one asks anyway (the reported
 * "Can you please make site like this" → "what kind of site?" exchange).
 *
 * Two things it must not do, both of which the obvious implementation gets
 * wrong. It must not TAKE: `takePendingAttachments` empties the queue on read,
 * and a gate that consumed it would leave the build with no files at all —
 * trading a wrong question for a lost upload. And it must not count files the
 * model cannot decode: a PDF never reaches it and an SVG reaches it only as an
 * embeddable URL, so neither supplies a visual brief and neither may stand the
 * gate down.
 *
 * The type test is inlined rather than imported from lib/ai/vision-attachments
 * so this module stays dependency-free for the client bundle; the two lists are
 * the same four types and a change to one is a change to both.
 */
const VISION_SUPPORTED_MIME = new Set([
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
]);

/** Is `mime` an image type the build model can actually decode? Exposed for
 *  call sites that count vision files from OTHER sources (uploaded Attachment
 *  records, restored IDB files) and must agree with this module about what
 *  counts. */
export function isVisionSupportedMimeType(mime: string | undefined): boolean {
  return mime != null && VISION_SUPPORTED_MIME.has(mime);
}

export function pendingVisionAttachmentCount(): number {
  return (pending ?? []).filter((f) => isVisionSupportedMimeType(f?.type))
    .length;
}

/** Where a pending-attachment census found its answer — logged so a blind
 *  first-turn decision is distinguishable from a genuinely attachment-less one. */
export type PendingAttachmentCensusSource = "memory" | "idb" | "none";

export interface PendingAttachmentCensus {
  /** Pending files the build model will be able to SEE (vision subset). */
  visionCount: number;
  /** Pending files of any type. */
  totalCount: number;
  source: PendingAttachmentCensusSource;
}

/**
 * The ASYNC census: how many attachments are pending for the first message,
 * looking at BOTH holders — the in-memory queue (soft nav) and the IndexedDB
 * mirror (hard nav / OAuth round-trip).
 *
 * `pendingVisionAttachmentCount()` above is synchronous and therefore blind to
 * IDB, which is exactly the hard-nav half of the "attached a design, got asked
 * what to build" regression: after a full-page redirect the memory holder is
 * empty, the files sit in IDB, and a sync peek reports zero. First-turn
 * routing must await this census instead — a text-only decision made before
 * attachment presence has resolved is the bug, not an optimization.
 *
 * PEEKS both holders, drains neither (see pendingVisionAttachmentCount's
 * docblock for why draining here would trade a wrong question for a lost
 * upload). The IDB read is read-only by construction.
 */
export async function resolvePendingAttachmentCensus(): Promise<PendingAttachmentCensus> {
  if ((pending?.length ?? 0) > 0) {
    return {
      visionCount: pendingVisionAttachmentCount(),
      totalCount: pending?.length ?? 0,
      source: "memory",
    };
  }
  try {
    const { peekPersistedAttachments } = await import(
      "./pending-attachments-idb"
    );
    const files = await peekPersistedAttachments();
    return {
      visionCount: files.filter((f) => isVisionSupportedMimeType(f?.type))
        .length,
      totalCount: files.length,
      source: files.length > 0 ? "idb" : "none",
    };
  } catch {
    return { visionCount: 0, totalCount: 0, source: "none" };
  }
}
